import { useCallback, useEffect, useState } from "react";
import { Gavel, Loader2, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Chip, Empty, Err, Panel } from "@/components/community/kit";
import { when } from "@/lib/community";

const CHOICES = [["yes", "For"], ["no", "Against"], ["abstain", "Abstain"]];

export const Motions = ({ community }) => {
  const cid = community.id;
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ title: "", body: "", closes_at: "", quorum: 0 });
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/communities/${cid}/proposals`);
      setData(res);
    } catch (e) { setError(errMsg(e)); }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, key) => {
    setBusy(key); setError("");
    try { await fn(); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return null;

  return (
    <Panel testId="community-motions" title="Motions"
      sub="Any member can put something to the community. Counts are live; the outcome is recorded."
      action={data.can_create && (
        <Button data-testid="motion-compose-toggle" size="sm" variant="outline"
          onClick={() => setComposing((v) => !v)}>
          {composing ? "Cancel" : <><Gavel className="mr-1.5 h-3.5 w-3.5" /> Raise a motion</>}
        </Button>
      )}>
      <Err testId="motions-error">{error}</Err>

      {composing && (
        <div className="mb-4 space-y-3 rounded-xl border border-ink-600 bg-ink-900/60 p-4">
          <Input data-testid="motion-title" placeholder="Set a minimum airport rate of $85"
            value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <Textarea data-testid="motion-body" rows={4} placeholder="What exactly is being proposed."
            value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-500">
              Voting closes
              <Input data-testid="motion-closes" type="datetime-local" className="mt-1"
                value={draft.closes_at}
                onChange={(e) => setDraft({ ...draft, closes_at: e.target.value })} />
            </label>
            <label className="text-xs text-slate-500">
              Quorum (0 = none)
              <Input data-testid="motion-quorum" type="number" min="0" className="mt-1"
                value={draft.quorum}
                onChange={(e) => setDraft({ ...draft, quorum: Number(e.target.value) })} />
            </label>
          </div>
          <Button data-testid="motion-create"
            disabled={draft.title.trim().length < 3 || !draft.body.trim() || busy === "create"}
            onClick={() => run(async () => {
              await api.post(`/communities/${cid}/proposals`, draft);
              setDraft({ title: "", body: "", closes_at: "", quorum: 0 });
              setComposing(false);
            }, "create")}>
            {busy === "create" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Put it to the members
          </Button>
        </div>
      )}

      {data.proposals.length === 0 ? (
        <Empty testId="motions-empty">No motions raised yet.</Empty>
      ) : (
        <ul className="space-y-3">
          {data.proposals.map((p) => (
            <li key={p.id} data-testid={`motion-${p.id}`}
              className="rounded-xl border border-ink-600 bg-ink-900/60 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-100">{p.title}</p>
                  <p className="mt-1 flex flex-wrap items-center gap-2">
                    <Chip testId={`motion-status-${p.id}`}
                      className={p.status === "closed"
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        : p.status === "expired" ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : ""}>
                      {p.outcome || p.status}
                    </Chip>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {p.author} · {p.cast}/{p.members} voted
                      {p.quorum ? ` · quorum ${p.quorum}` : ""}
                      {p.closes_at && p.status === "open" ? ` · closes ${when(p.closes_at)}` : ""}
                    </span>
                  </p>
                </div>
                {data.can_manage && (
                  <div className="flex gap-2">
                    {p.status === "open" && (
                      <Button data-testid={`motion-close-${p.id}`} size="sm" variant="outline"
                        onClick={() => run(async () => {
                          await api.post(`/communities/${cid}/proposals/${p.id}/close`);
                        }, `close-${p.id}`)}>
                        Close &amp; record
                      </Button>
                    )}
                    <Button data-testid={`motion-delete-${p.id}`} size="sm" variant="ghost"
                      className="text-slate-400 hover:text-red-300"
                      onClick={() => run(async () => {
                        await api.delete(`/communities/${cid}/proposals/${p.id}`);
                      }, `del-${p.id}`)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-400">{p.body}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {CHOICES.map(([key, label]) => (
                  <button key={key} data-testid={`motion-vote-${key}-${p.id}`}
                    disabled={p.status !== "open" || busy === `${p.id}-${key}`}
                    onClick={() => run(async () => {
                      await api.post(`/communities/${cid}/proposals/${p.id}/vote`, { choice: key });
                    }, `${p.id}-${key}`)}
                    className={`rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${
                      p.my_vote === key ? "border-gold/40 bg-gold/10 text-gold"
                        : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
                    {label} · {p.counts?.[key] || 0}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};
